import { QuoteDiscount } from '@brenger/api-client';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useTypedSelector } from '../../../hooks';
import { basisPointsAsString, priceAsString } from '../../../utils/price';
import { getIsExcludingVat } from '../../User/ducks';

type DiscountSummaryVariant = 'sidebar' | 'terms';

interface Props {
  discount: QuoteDiscount;
  variant?: DiscountSummaryVariant;
}

/* Both surfaces show the same two lines, they only differ in how their price rows are laid out */
const ROW_CLASS: Record<DiscountSummaryVariant, string> = {
  sidebar: 'summary-line',
  terms: 'flex justify-between',
};

export const DiscountSummary = ({ discount, variant = 'sidebar' }: Props): JSX.Element | null => {
  const isExclVat = useTypedSelector(getIsExcludingVat);
  const vatKey = isExclVat ? 'excl_vat' : 'incl_vat';
  const originalPrice = discount.original_price?.[vatKey];
  const discountAmount = discount.discount_amount?.[vatKey];

  if (!originalPrice || !discountAmount) {
    return null;
  }

  const rowClass = ROW_CLASS[variant];
  /* Keep adds-price on the sidebar so summary--hide-prices still conceals these amounts */
  const priceClass = variant === 'sidebar' ? 'adds-price' : undefined;

  return (
    <div data-qa-id="discount-summary">
      <div className={rowClass}>
        <div>
          <Translate id={'request_flow.price.total_before_discount'} />
        </div>
        <div className={priceClass} style={{ fontWeight: 'bold' }}>
          <span className={'text-black'}>{priceAsString({ price: originalPrice, addPlusMinusIndicator: false })}</span>
        </div>
      </div>
      <div className={rowClass}>
        <div>
          <Translate
            id={'request_flow.price.volume_discount'}
            data={{
              percentage: (
                <span className={'text-green-500'}>{basisPointsAsString(discount.percentage_basis_points)}%</span>
              ),
            }}
          />
        </div>
        <div className={priceClass}>
          {/* Pricing reports the deducted amount as a positive number, we always present it as a deduction */}
          <span className={'text-green-500'}>
            {priceAsString({ price: { ...discountAmount, amount: -Math.abs(discountAmount.amount) } })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DiscountSummary;
